import { createGrid, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import Papa from 'papaparse';
import 'bulma/css/bulma.min.css';
import './styles.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const API_BASE    = 'https://bot.huertomatizado.com/server';
const API_URL     = `${API_BASE}/api/records`;
const AUTH_URL    = `${API_BASE}/api/auth`;
const ACCOUNTS_URL = `${API_BASE}/api/accounts`;

const TIPOS = ['Saldo_inicial', 'Venta', 'Compra', 'Retiro_dueno'];
const CATEGORIAS = ['Servicios', 'Operativo', 'Viaticos', 'Retiro'];
const CURRENCY_FORMATTER = new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC' });

class App {
  constructor() {
    this.rowData        = [];
    this.accounts       = [];
    this.currentAccountId = null;
    this.gridApi        = null;
    this.editingRowId   = null;
    this.isAuthenticated = false;
  }

  init() {
    Auth.init();
    
    if (localStorage.getItem('app_auth') === 'true') {
      this.showMainContent();
      this.run();
    }
  }

  async run() {
    try {
      this.accounts = await AccountsApi.fetchAll();
    } catch {
      this.accounts = [];
    }

    if (this.accounts.length > 0) {
      this.currentAccountId = this.accounts[0].id;
    }

    Tabs.render(this.accounts, this.currentAccountId);

    await this.loadRecords();
    Events.setup();
  }

  async loadRecords() {
    try {
      this.rowData = await RecordsApi.fetchAll(this.currentAccountId);
    } catch {
      alert('No se pudo conectar con el servidor');
      this.rowData = [];
    }

    Filters.init(this.rowData);
    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.rowData);
      Summary.update(this.rowData);
    } else {
      Grid.init(this.rowData);
      Summary.update(this.rowData);
    }
    // Auto-seleccionar mes actual y filtrar summary + grid
    Filters.selectCurrentMonth();
  }

  async switchAccount(accountId) {
    this.currentAccountId = accountId;
    const account = this.accounts.find(a => a.id === accountId);
    Tabs.setActive(accountId);
    Tabs.applyColor(account?.color || '#00d1b2');
    await this.loadRecords();
  }

  showMainContent() {
    DOM.hide('pin-screen');
    DOM.show('main-content');
  }
}

class DOM {
  static show(id, display = 'block') {
    const el = document.getElementById(id);
    if (el) el.style.display = display;
  }

  static hide = (id) => this.show(id, 'none');

  static addClass(id, className) {
    const el = document.getElementById(id);
    if (el) el.classList.add(className);
  }

  static removeClass(id, className) {
    const el = document.getElementById(id);
    if (el) el.classList.remove(className);
  }

  static setText(id, text) {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  }

  static getValue(id) {
    const el = document.getElementById(id);
    return el ? el.value : '';
  }

  static setValue(id, value) {
    const el = document.getElementById(id);
    if (el) el.value = value;
  }
}

class Formatters {
  static currency(value) {
    return CURRENCY_FORMATTER.format(value);
  }

  static parseAmount(val) {
    if (val === null || val === undefined || val === '') return 0;
    return parseFloat(String(val).replace(/,/g, '').replace(/\s/g, '')) || 0;
  }

  static formatDate(value) {
    if (!value) return '';
    const d = new Date(value + 'T00:00:00');
    return d.toLocaleDateString('es-CR');
  }

  static moneyCell = (p) => (this.parseAmount(p.value) > 0 ? this.currency(this.parseAmount(p.value)) : '');
  
  static moneyStyle = () => ({ fontWeight: '600', color: '#2e7d32' });
  
  static expenseStyle = () => ({ fontWeight: '600', color: '#c62828' });
  
  static balanceStyle = (p) => ({
    fontWeight: '600',
    color: this.parseAmount(p.value) >= 0 ? '#1565c0' : '#c62828',
  });
  
  static saldoCell = (p) => (p.value !== '' && p.value !== null ? this.currency(this.parseAmount(p.value)) : '');
}

class Auth {
  static init() {
    const pinInput = document.getElementById('pin-input');
    const pinSubmit = document.getElementById('btn-pin-submit');
    const requestBtn = document.getElementById('btn-request-code');

    requestBtn.addEventListener('click', async () => this.handleRequestCode(requestBtn, pinInput));
    pinInput.addEventListener('keypress', (e) => { if (e.key === 'Enter') pinSubmit.click(); });
    pinSubmit.addEventListener('click', async () => this.handlePinSubmit(pinInput, pinSubmit));
  }

  static async handleRequestCode(btn, input) {
    btn.disabled = true;
    btn.textContent = 'Enviando...';

    try {
      const result = await AuthApi.requestCode();
      if (result.success) {
        DOM.show('code-sent');
        DOM.hide('pin-error');
        input.focus();
      } else {
        DOM.setText('pin-error', result.error || 'Error al enviar PIN');
        DOM.show('pin-error');
      }
    } catch (err) {
      DOM.setText('pin-error', 'Error de conexión');
      DOM.show('pin-error');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Solicitar PIN';
    }
  }

  static async handlePinSubmit(input, btn) {
    const code = input.value.trim();
    
    if (code.length !== 6 || !/^\d+$/.test(code)) {
      DOM.setText('pin-error', 'El PIN debe tener 6 dígitos');
      DOM.show('pin-error');
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Validando...';

    try {
      const valid = await AuthApi.validatePin(code);
      
      if (valid) {
        localStorage.setItem('app_auth', 'true');
        app.showMainContent();
        app.run();
      } else {
        DOM.setText('pin-error', 'PIN incorrecto o expirado');
        DOM.show('pin-error');
        input.value = '';
        input.focus();
      }
    } catch (err) {
      DOM.setText('pin-error', 'Error de conexión');
      DOM.show('pin-error');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Entrar';
    }
  }
}

class RecordsApi {
  static async fetchAll(accountId = null) {
    const url = accountId ? `${API_URL}?account_id=${accountId}` : API_URL;
    const data = await this.request(url);
    return data.map(this.normalizeRecord);
  }

  static async create(record) {
    return this.normalizeRecord(await this.request(API_URL, {
      method: 'POST',
      body: JSON.stringify(record),
    }));
  }

  static async update(id, record) {
    return this.normalizeRecord(await this.request(`${API_URL}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(record),
    }));
  }

  static async delete(ids) {
    return this.request(API_URL, {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
  }

  static async bulkCreate(records) {
    const data = await this.request(`${API_URL}/bulk`, {
      method: 'POST',
      body: JSON.stringify({ records }),
    });
    return data.map(this.normalizeRecord);
  }

  static async request(url, options = {}) {
    const res = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...options.headers },
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Error en la solicitud');
    }
    return res.json();
  }

  static normalizeRecord(r) {
    return {
      ...r,
      ingreso: Formatters.parseAmount(r.ingreso),
      gasto: Formatters.parseAmount(r.gasto),
      saldo: Formatters.parseAmount(r.saldo),
    };
  }
}

class AuthApi {
  static async requestCode() {
    const res = await fetch(`${AUTH_URL}/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    return res.json();
  }

  static async validatePin(code) {
    const res = await fetch(AUTH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const data = await res.json();
    return data.authenticated === true;
  }
}

class AccountsApi {
  static async fetchAll() {
    const res = await fetch(ACCOUNTS_URL);
    return res.json();
  }

  static async create(name, color) {
    const res = await fetch(ACCOUNTS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, color }),
    });
    if (!res.ok) throw new Error('Error creando cuenta');
    return res.json();
  }

  static async update(id, name, color) {
    const res = await fetch(`${ACCOUNTS_URL}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, color }),
    });
    if (!res.ok) throw new Error('Error actualizando cuenta');
    return res.json();
  }

  static async delete(id) {
    const res = await fetch(`${ACCOUNTS_URL}/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Error eliminando cuenta');
    }
    return res.json();
  }
}

class Tabs {
  static render(accounts, activeId) {
    const container = document.getElementById('accounts-tabs');
    if (!container) return;
    container.innerHTML = '';

    accounts.forEach(acc => {
      const li = document.createElement('li');
      li.dataset.id = acc.id;
      if (acc.id === activeId) li.classList.add('is-active');

      const a = document.createElement('a');
      a.textContent = acc.name;
      if (acc.id === activeId) {
        a.style.borderBottomColor = acc.color;
        a.style.color = acc.color;
      }

      a.addEventListener('click', () => app.switchAccount(acc.id));

      // Right-click to rename/delete
      a.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        Tabs.openAccountMenu(acc);
      });

      li.appendChild(a);
      container.appendChild(li);
    });

    // "+" button
    const addLi = document.createElement('li');
    const addA = document.createElement('a');
    addA.innerHTML = '<strong>+</strong>';
    addA.title = 'Nueva cuenta';
    addA.addEventListener('click', () => Tabs.openNewAccountModal());
    addLi.appendChild(addA);
    container.appendChild(addLi);

    // Apply color of active account
    const active = accounts.find(a => a.id === activeId);
    if (active) Tabs.applyColor(active.color);
  }

  static setActive(accountId) {
    document.querySelectorAll('#accounts-tabs li').forEach(li => {
      const isActive = parseInt(li.dataset.id) === accountId;
      li.classList.toggle('is-active', isActive);
      const a = li.querySelector('a');
      if (a && li.dataset.id) {
        const acc = app.accounts.find(ac => ac.id === parseInt(li.dataset.id));
        a.style.borderBottomColor = isActive ? (acc?.color || '') : '';
        a.style.color = isActive ? (acc?.color || '') : '';
      }
    });
  }

  static applyColor(color) {
    document.documentElement.style.setProperty('--account-color', color);
  }

  static openNewAccountModal() {
    DOM.show('account-modal');
    document.getElementById('account-modal-title').textContent = 'Nueva cuenta';
    document.getElementById('account-form').reset();
    document.getElementById('account-color').value = '#00d1b2';
    app._editingAccountId = null;
  }

  static openAccountMenu(acc) {
    const action = prompt(`Cuenta: "${acc.name}"\n\nEscribe "renombrar" o "eliminar":`);
    if (!action) return;
    if (action.toLowerCase() === 'renombrar') {
      const newName = prompt('Nuevo nombre:', acc.name);
      if (newName && newName.trim()) {
        AccountsApi.update(acc.id, newName.trim(), acc.color).then(updated => {
          const idx = app.accounts.findIndex(a => a.id === acc.id);
          if (idx !== -1) app.accounts[idx] = updated;
          Tabs.render(app.accounts, app.currentAccountId);
        }).catch(e => alert(e.message));
      }
    } else if (action.toLowerCase() === 'eliminar') {
      if (confirm(`¿Eliminar cuenta "${acc.name}"? Sus registros quedarán sin cuenta.`)) {
        AccountsApi.delete(acc.id).then(() => {
          app.accounts = app.accounts.filter(a => a.id !== acc.id);
          if (app.currentAccountId === acc.id) {
            app.currentAccountId = app.accounts[0]?.id || null;
          }
          Tabs.render(app.accounts, app.currentAccountId);
          app.loadRecords();
        }).catch(e => alert(e.message));
      }
    }
  }

  static async saveAccount() {
    const name  = document.getElementById('account-name').value.trim();
    const color = document.getElementById('account-color').value;
    if (!name) { alert('Nombre requerido'); return; }

    try {
      if (app._editingAccountId) {
        const updated = await AccountsApi.update(app._editingAccountId, name, color);
        const idx = app.accounts.findIndex(a => a.id === app._editingAccountId);
        if (idx !== -1) app.accounts[idx] = updated;
      } else {
        const created = await AccountsApi.create(name, color);
        app.accounts.push(created);
        app.currentAccountId = created.id;
      }
      DOM.hide('account-modal');
      Tabs.render(app.accounts, app.currentAccountId);
      app.loadRecords();
    } catch (e) {
      alert(e.message);
    }
  }
}

class DatePickerEditor {
  init(params) {
    this.params = params;
    this.originalValue = params.value;
    this.value = params.value;

    this.container = document.createElement('div');
    this.container.className = 'date-picker-editor';

    this.input = document.createElement('input');
    this.input.type = 'date';
    this.input.value = params.value || '';
    this.input.className = 'date-picker-input';

    const btnRow = document.createElement('div');
    btnRow.className = 'date-picker-actions';

    this.applyBtn = document.createElement('button');
    this.applyBtn.textContent = 'Apply';
    this.applyBtn.className = 'button is-primary is-small';

    this.cancelBtn = document.createElement('button');
    this.cancelBtn.textContent = 'Cancel';
    this.cancelBtn.className = 'button is-light is-small';

    this.statusEl = document.createElement('span');
    this.statusEl.className = 'date-picker-status';

    this.applyBtn.addEventListener('click', () => this._apply());
    this.cancelBtn.addEventListener('click', () => params.stopEditing(true));

    btnRow.appendChild(this.applyBtn);
    btnRow.appendChild(this.cancelBtn);
    this.container.appendChild(this.input);
    this.container.appendChild(btnRow);
    this.container.appendChild(this.statusEl);
  }

  async _apply() {
    const newDate = this.input.value;
    if (!newDate) return;
    if (newDate === this.originalValue) {
      this.params.stopEditing(true);
      return;
    }
    this.applyBtn.disabled = true;
    this.applyBtn.textContent = '...';
    this.statusEl.textContent = '';
    try {
      const data = this.params.data;
      const updated = await RecordsApi.update(data.id, { ...data, fecha: newDate });
      this.value = newDate;
      Grid.updateRow(data, updated);
      this.params.stopEditing();
    } catch (err) {
      this.statusEl.textContent = '⚠️ Error al guardar';
      this.applyBtn.disabled = false;
      this.applyBtn.textContent = 'Apply';
    }
  }

  getGui()           { return this.container; }
  getValue()         { return this.value; }
  afterGuiAttached() { this.input.focus(); }
  isCancelAfterEnd() { return true; }
}

const GridColumns = {
  tipoTagMap: {
    Venta: 'tag is-success is-light',
    Compra: 'tag is-danger is-light',
    Saldo_inicial: 'tag is-info is-light',
    Retiro_dueno: 'tag is-warning is-light',
  },

  categoriaColors: {
    Servicios: { background: '#e0f7fa', color: '#006064' },
    Operativo: { background: '#e8f4fd', color: '#1565c0' },
    Viaticos: { background: '#fff3e0', color: '#e65100' },
    Retiro: { background: '#fce4ec', color: '#880e4f' },
  },

  getColumns() {
    return [
      {
        field: 'id',
        headerName: '#',
        width: 65,
        pinned: 'left',
        sortable: true,
        filter: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
      },
      {
        field: 'fecha',
        headerName: 'Fecha',
        width: 120,
        sortable: true,
        filter: 'agDateColumnFilter',
        valueFormatter: (p) => Formatters.formatDate(p.value),
        editable: true,
        cellEditor: DatePickerEditor,
        cellEditorPopup: true,
        cellClass: 'fecha-editable',
      },
      {
        field: 'tipo',
        headerName: 'Tipo',
        width: 140,
        sortable: true,
        filter: 'agTextColumnFilter',
        cellRenderer: (p) => `<span class="${this.tipoTagMap[p.value] || 'tag is-light'}">${p.value || ''}</span>`,
      },
      {
        field: 'categoria',
        headerName: 'Categoría',
        width: 130,
        sortable: true,
        filter: 'agTextColumnFilter',
        cellStyle: (p) => this.categoriaColors[p.value] || null,
      },
      {
        field: 'concepto',
        headerName: 'Concepto',
        flex: 1,
        minWidth: 180,
        sortable: true,
        filter: 'agTextColumnFilter',
        tooltipField: 'concepto',
      },
      {
        field: 'moneda',
        headerName: 'Moneda',
        width: 90,
        sortable: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'ingreso',
        headerName: 'Ingreso',
        width: 140,
        sortable: true,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: Formatters.moneyCell,
        cellStyle: Formatters.moneyStyle,
      },
      {
        field: 'gasto',
        headerName: 'Gasto',
        width: 140,
        sortable: true,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: Formatters.moneyCell,
        cellStyle: Formatters.expenseStyle,
      },
      {
        field: 'medio_de_pago',
        headerName: 'Medio de pago',
        width: 140,
        sortable: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'saldo',
        headerName: 'Saldo',
        width: 150,
        sortable: true,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        valueFormatter: Formatters.saldoCell,
        cellStyle: Formatters.balanceStyle,
      },
      {
        headerName: 'Acciones',
        width: 110,
        sortable: false,
        filter: false,
        pinned: 'right',
        cellRenderer: (p) => {
          const btn = document.createElement('button');
          btn.className = 'button is-warning is-small';
          btn.textContent = 'Editar';
          btn.addEventListener('click', () => Modals.openEdit(p.data));
          return btn;
        },
      },
    ];
  }
};

class Grid {
  static init(rowData) {
    const gridDiv = document.querySelector('#myGrid');

    const gridOptions = {
      columnDefs: GridColumns.getColumns(),
      rowData,
      getRowId: (params) => String(params.data.id),
      rowSelection: { mode: 'multiRow' },
      defaultColDef: { resizable: true },
      animateRows: true,
      pagination: true,
      paginationPageSize: 20,
      paginationPageSizeSelector: [10, 20, 50, 100],
      enableCellTextSelection: true,
      onSelectionChanged: () => {
        const selected = app.gridApi.getSelectedRows();
        document.getElementById('btn-delete-row').disabled = selected.length === 0;
      },
    };

    app.gridApi = createGrid(gridDiv, gridOptions);
  }

  static updateRow(row, data) {
    const idx = app.rowData.findIndex((r) => r.id === row.id);
    if (idx !== -1) app.rowData[idx] = data;
    app.gridApi.applyTransaction({ update: [data] });
  }

  static addRows(rows) {
    app.rowData.push(...rows);
    app.gridApi.applyTransaction({ add: rows });
  }

  static removeRows(rows) {
    const idSet = new Set(rows.map(r => r.id));
    app.rowData = app.rowData.filter((r) => !idSet.has(r.id));
    app.gridApi.applyTransaction({ remove: rows });
  }
}

class Modals {
  static openAdd() {
    app.editingRowId = null;
    DOM.setText('modal-title', 'Nuevo Registro');
    document.getElementById('record-form').reset();
    DOM.setValue('form-fecha', new Date().toISOString().split('T')[0]);
    DOM.setValue('form-moneda', 'CRC');
    DOM.addClass('record-modal', 'is-active');
  }

  static openEdit(data) {
    app.editingRowId = data.id;
    DOM.setText('modal-title', 'Editar Registro');
    DOM.setValue('form-fecha', data.fecha);
    DOM.setValue('form-tipo', data.tipo);
    DOM.setValue('form-categoria', data.categoria);
    DOM.setValue('form-concepto', data.concepto);
    DOM.setValue('form-moneda', data.moneda);
    DOM.setValue('form-ingreso', data.ingreso || '');
    DOM.setValue('form-gasto', data.gasto || '');
    DOM.setValue('form-medio-pago', data.medio_de_pago);
    DOM.setValue('form-saldo', data.saldo !== undefined ? data.saldo : '');
    DOM.addClass('record-modal', 'is-active');
  }

  static close() {
    DOM.removeClass('record-modal', 'is-active');
    app.editingRowId = null;
  }

  static getFormData() {
    return {
      fecha: DOM.getValue('form-fecha'),
      tipo: DOM.getValue('form-tipo'),
      categoria: DOM.getValue('form-categoria'),
      concepto: DOM.getValue('form-concepto').trim(),
      moneda: DOM.getValue('form-moneda'),
      ingreso: Formatters.parseAmount(DOM.getValue('form-ingreso')),
      gasto: Formatters.parseAmount(DOM.getValue('form-gasto')),
      medio_de_pago: DOM.getValue('form-medio-pago'),
      saldo: Formatters.parseAmount(DOM.getValue('form-saldo')),
      account_id: app.currentAccountId,
    };
  }

  static async save() {
    const form = document.getElementById('record-form');
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const recordData = this.getFormData();

    try {
      if (app.editingRowId !== null) {
        const updated = await RecordsApi.update(app.editingRowId, recordData);
        Grid.updateRow({ id: app.editingRowId }, updated);
      } else {
        const created = await RecordsApi.create(recordData);
        Grid.addRows([created]);
      }
      Summary.update(app.rowData);
      this.close();
    } catch (err) {
      const msg = err.message.toLowerCase();
      if (msg.includes('not found') || msg.includes('no encontrado') || msg.includes('404')) {
        alert('El registro ya no existe en el servidor. Por favor, recarga la página.');
        app.rowData = await RecordsApi.fetchAll();
        app.gridApi.setGridOption('rowData', app.rowData);
        Summary.update(app.rowData);
      } else {
        alert('Error al guardar: ' + err.message);
      }
    }
  }
}

class Actions {
  static async deleteSelected() {
    const selected = app.gridApi.getSelectedRows();
    if (selected.length === 0) return;
    if (!confirm(`¿Eliminar ${selected.length} registro(s)?`)) return;

    const ids = selected.map((r) => r.id);
    try {
      await RecordsApi.delete(ids);
      Grid.removeRows(selected);
      Summary.update(app.rowData);
      document.getElementById('btn-delete-row').disabled = true;
    } catch (err) {
      alert('Error al eliminar: ' + err.message);
    }
  }

  static exportCSV() {
    const exportData = app.rowData.map(({ id: _id, created_at: _ca, ...r }) => ({
      Fecha: r.fecha,
      Tipo: r.tipo,
      Categoria: r.categoria,
      Concepto: r.concepto,
      Moneda: r.moneda,
      Ingreso: r.ingreso || '',
      Gasto: r.gasto || '',
      Medio_de_pago: r.medio_de_pago,
      Saldo: r.saldo !== undefined ? r.saldo : '',
    }));
    const csv = Papa.unparse(exportData, { header: true });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registros_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  static importCSV(file) {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        const newRows = result.data.map((row) => ({
          fecha: row['Fecha'] || row['fecha'] || '',
          tipo: row['Tipo'] || row['tipo'] || '',
          categoria: row['Categoria'] || row['categoria'] || '',
          concepto: row['Concepto'] || row['concepto'] || '',
          moneda: row['Moneda'] || row['moneda'] || 'CRC',
          ingreso: Formatters.parseAmount(row['Ingreso'] || row['ingreso']),
          gasto: Formatters.parseAmount(row['Gasto'] || row['gasto']),
          medio_de_pago: row['Medio_de_pago'] || row['medio_de_pago'] || '',
          saldo: Formatters.parseAmount(row['Saldo'] || row['saldo']),
        }));
        try {
          const created = await RecordsApi.bulkCreate(newRows);
          Grid.addRows(created);
          Summary.update(app.rowData);
        } catch (err) {
          alert('Error al importar CSV: ' + err.message);
        }
      },
    });
  }
}

class Summary {
  static update(rowData) {
    const totalIngreso = rowData.reduce((sum, r) => sum + Formatters.parseAmount(r.ingreso), 0);
    const totalGasto = rowData.reduce((sum, r) => sum + Formatters.parseAmount(r.gasto), 0);
    const balance = totalIngreso - totalGasto;

    DOM.setText('total-ingreso', Formatters.currency(totalIngreso));
    DOM.setText('total-gasto', Formatters.currency(totalGasto));
    DOM.setText('total-records', rowData.length);
    DOM.setText('balance', Formatters.currency(balance));

    const balanceEl = document.getElementById('balance');
    balanceEl.className = 'title is-4 ' + (balance >= 0 ? 'has-text-success' : 'has-text-danger');
  }
}

class Filters {
  static init(rowData) {
    this.populateSelect('filter-tipo', TIPOS);
    this.populateSelect('filter-categoria', CATEGORIAS);
    this.populateMesFilter(rowData);
  }

  static populateSelect(selectId, options) {
    const select = document.getElementById(selectId);
    if (!select) return;
    select.innerHTML = '';
    options.forEach((opt) => {
      const option = document.createElement('option');
      option.value = opt;
      option.textContent = opt;
      select.appendChild(option);
    });
  }

  static populateMesFilter(rowData) {
    const mesesUnicos = new Set();
    rowData.forEach((r) => {
      if (r.fecha) {
        const d = new Date(r.fecha + 'T00:00:00');
        if (!isNaN(d)) {
          const mes = d.toLocaleDateString('es-CR', { month: 'short' }).replace('.', '');
          const anio = d.getFullYear().toString().slice(-2);
          mesesUnicos.add(`${mes.toLowerCase()}-${anio}`);
        }
      }
    });

    const mesesOrder = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const mesesOrdenados = Array.from(mesesUnicos).sort((a, b) => {
      const [mesA, anioA] = a.split('-');
      const [mesB, anioB] = b.split('-');
      const ordenA = parseInt(anioA) * 100 + mesesOrder.indexOf(mesA);
      const ordenB = parseInt(anioB) * 100 + mesesOrder.indexOf(mesB);
      return ordenA - ordenB;
    });

    const select = document.getElementById('filter-mes');
    if (!select) return;
    select.innerHTML = '';

    // Primera opción: Todo
    const allOpt = document.createElement('option');
    allOpt.value = '';
    allOpt.textContent = 'Todo';
    select.appendChild(allOpt);

    mesesOrdenados.forEach((m) => {
      const opt = document.createElement('option');
      opt.value = m;
      opt.textContent = m.charAt(0).toUpperCase() + m.slice(1);
      select.appendChild(opt);
    });
  }

  static selectCurrentMonth() {
    const select = document.getElementById('filter-mes');
    if (!select) return;
    const now = new Date();
    const mes = now.toLocaleDateString('es-CR', { month: 'short' }).replace('.', '').toLowerCase();
    const anio = now.getFullYear().toString().slice(-2);
    const current = `${mes}-${anio}`;
    const match = Array.from(select.options).find(o => o.value === current);
    if (match) {
      select.value = current;
      select.dispatchEvent(new Event('change'));
    }
  }
}

class Events {
  static setup() {
    const qf = document.getElementById('quick-filter');
    qf.addEventListener('input', () => app.gridApi.setGridOption('quickFilterText', qf.value));

    document.getElementById('btn-clear-filter').addEventListener('click', () => {
      qf.value = '';
      app.gridApi.setGridOption('quickFilterText', '');
      document.getElementById('filter-tipo').value = '';
      document.getElementById('filter-categoria').value = '';
      document.getElementById('filter-mes').value = '';
      app.gridApi.setFilterModel(null);
    });

    this.setupSelectFilter('filter-tipo', 'tipo');
    this.setupSelectFilter('filter-categoria', 'categoria');
    this.setupMesFilter();

    document.getElementById('btn-add-row').addEventListener('click', Modals.openAdd);
    document.getElementById('btn-delete-row').addEventListener('click', Actions.deleteSelected);
    document.getElementById('modal-close').addEventListener('click', Modals.close);
    document.getElementById('btn-cancel-modal').addEventListener('click', Modals.close);
    document.getElementById('btn-save-record').addEventListener('click', () => Modals.save());
    document.querySelector('.modal-background').addEventListener('click', Modals.close);
    document.getElementById('btn-export-csv').addEventListener('click', Actions.exportCSV);

    // Account modal events
    document.getElementById('btn-save-account').addEventListener('click', () => Tabs.saveAccount());
    document.getElementById('btn-cancel-account').addEventListener('click', () => DOM.hide('account-modal'));
    document.getElementById('account-modal-close').addEventListener('click', () => DOM.hide('account-modal'));
    document.getElementById('account-modal-bg').addEventListener('click', () => DOM.hide('account-modal'));

    document.getElementById('btn-import-csv').addEventListener('click', () =>
      document.getElementById('file-import').click()
    );
    document.getElementById('file-import').addEventListener('change', (e) => {
      if (e.target.files[0]) {
        Actions.importCSV(e.target.files[0]);
        e.target.value = '';
      }
    });
  }

  static setupSelectFilter(selectId, field) {
    document.getElementById(selectId).addEventListener('change', (e) => {
      if (e.target.value) {
        app.gridApi.setFilterModel({ [field]: { filterType: 'text', type: 'equals', filter: e.target.value } });
      } else {
        const current = app.gridApi.getFilterModel();
        delete current[field];
        app.gridApi.setFilterModel(current);
      }
    });
  }

  static setupMesFilter() {
    document.getElementById('filter-mes').addEventListener('change', (e) => {
      const mesSeleccionado = e.target.value;
      const mesesIndex = { ene: '01', feb: '02', mar: '03', abr: '04', may: '05', jun: '06', jul: '07', ago: '08', sep: '09', oct: '10', nov: '11', dic: '12' };

      if (mesSeleccionado) {
        const partes = mesSeleccionado.toLowerCase().split('-');
        const mes = partes[0];
        const anio = '20' + partes[1];
        const mesNum = mesesIndex[mes];

        if (mesNum) {
          const datePrefix = `${anio}-${mesNum}`;
          app.gridApi.setGridOption('quickFilterText', datePrefix);
          // Filtrar summary cards al mes seleccionado
          const filtered = app.rowData.filter(r => r.fecha && r.fecha.startsWith(datePrefix));
          Summary.update(filtered);
        }
      } else {
        app.gridApi.setGridOption('quickFilterText', '');
        // Summary con todos los registros
        Summary.update(app.rowData);
      }
    });
  }
}

const app = new App();

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(new URL('../sw.js', import.meta.url)).catch(() => {});
  });
}

app.init();
